'use client'

import { Input } from '@/components/forms/inputs/text/Input'
import { Badge } from '@/components/ui/common/Badge'
import { Button } from '@/components/ui/common/Button'
import { cn } from '@/utils/common'
import {
  BookOpen,
  Briefcase,
  ChevronRight,
  DollarSign,
  GraduationCap,
  Heart,
  MapPin,
  Search,
  Target,
  TrendingUp,
  Users
} from 'lucide-react'
import { useState } from 'react'

interface Profession {
  id: number
  name: string
  category: string
  description: string
  requirements: string[]
  skills: string[]
  averageSalary: string
  demandLevel: 'Wysoki' | 'Średni' | 'Niski'
  educationPath: string
  workEnvironment: string
  careerProspects: string
  matchPercentage: number
  isFavorite: boolean
  requiredSubjects: string[]
  universities: string[]
}

export default function ProposedProfessions() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('Wszystkie')
  const [selectedProfession, setSelectedProfession] = useState<Profession | null>(null)

  const professions: Profession[] = [
    {
      id: 1,
      name: 'Programista / Developer',
      category: 'IT i Technologie',
      description:
        'Tworzenie aplikacji, stron internetowych i systemów informatycznych. Praca z różnymi językami programowania i technologiami.',
      requirements: [
        'Znajomość języków programowania',
        'Logiczne myślenie',
        'Umiejętność rozwiązywania problemów',
        'Ciągłe uczenie się nowych technologii'
      ],
      skills: ['JavaScript', 'Python', 'React', 'Bazy danych', 'Git'],
      averageSalary: '8 000 - 15 000 zł',
      demandLevel: 'Wysoki',
      educationPath: 'Informatyka, Inżynieria Oprogramowania, kursy programistyczne',
      workEnvironment: 'Biuro, praca zdalna, elastyczne godziny',
      careerProspects:
        'Bardzo dobre perspektywy rozwoju, możliwość specjalizacji w różnych obszarach',
      matchPercentage: 95,
      isFavorite: false,
      requiredSubjects: ['Matematyka (rozszerzona)', 'Informatyka', 'Fizyka'],
      universities: ['Politechnika Warszawska', 'AGH Kraków', 'Uniwersytet Warszawski']
    },
    {
      id: 2,
      name: 'Psycholog',
      category: 'Zdrowie i Opieka',
      description:
        'Diagnozowanie i leczenie zaburzeń psychicznych, prowadzenie terapii, badania nad zachowaniem człowieka.',
      requirements: [
        'Empatia i umiejętności interpersonalne',
        'Odporność psychiczna',
        'Umiejętność słuchania',
        'Cierpliwość i wytrwałość'
      ],
      skills: ['Terapia', 'Diagnostyka', 'Komunikacja', 'Analiza', 'Badania'],
      averageSalary: '4 500 - 8 000 zł',
      demandLevel: 'Średni',
      educationPath: 'Psychologia (5 lat), specjalizacja, praktyki',
      workEnvironment: 'Gabinet prywatny, szpital, poradnia, szkoła',
      careerProspects: 'Stabilne perspektywy, możliwość własnej praktyki',
      matchPercentage: 88,
      isFavorite: true,
      requiredSubjects: ['Biologia', 'Język polski', 'Matematyka'],
      universities: ['Uniwersytet Warszawski', 'Uniwersytet Jagielloński', 'SWPS']
    },
    {
      id: 3,
      name: 'Inżynier Mechanik',
      category: 'Inżynieria',
      description:
        'Projektowanie, konstruowanie i nadzorowanie produkcji maszyn, urządzeń i systemów mechanicznych.',
      requirements: [
        'Znajomość matematyki i fizyki',
        'Myślenie techniczne',
        'Precyzja i dokładność',
        'Umiejętność pracy z CAD'
      ],
      skills: [
        'AutoCAD',
        'SolidWorks',
        'Mechanika',
        'Termodynamika',
        'Materiałoznawstwo'
      ],
      averageSalary: '6 000 - 12 000 zł',
      demandLevel: 'Wysoki',
      educationPath: 'Mechanika i Budowa Maszyn, Inżynieria Mechaniczna',
      workEnvironment: 'Biuro projektowe, hala produkcyjna, laboratorium',
      careerProspects:
        'Dobre perspektywy w przemyśle, możliwość rozwoju w kierunku managementu',
      matchPercentage: 82,
      isFavorite: false,
      requiredSubjects: [
        'Matematyka (rozszerzona)',
        'Fizyka (rozszerzona)',
        'Informatyka'
      ],
      universities: [
        'Politechnika Warszawska',
        'Politechnika Krakowska',
        'Politechnika Gdańska'
      ]
    },
    {
      id: 4,
      name: 'Grafik / Designer',
      category: 'Kreatywne',
      description:
        'Tworzenie projektów graficznych, identyfikacji wizualnej, materiałów reklamowych i interfejsów użytkownika.',
      requirements: [
        'Kreatywność i wyobraźnia',
        'Znajomość programów graficznych',
        'Poczucie estetyki',
        'Umiejętność pracy pod presją czasu'
      ],
      skills: ['Photoshop', 'Illustrator', 'InDesign', 'Figma', 'Typografia'],
      averageSalary: '4 000 - 9 000 zł',
      demandLevel: 'Średni',
      educationPath: 'Grafika, Wzornictwo, Multimedia, kursy specjalistyczne',
      workEnvironment: 'Agencja reklamowa, studio graficzne, freelancing',
      careerProspects: 'Możliwość specjalizacji w UX/UI, brandingu, ilustracji',
      matchPercentage: 76,
      isFavorite: false,
      requiredSubjects: ['Plastyka', 'Informatyka', 'Język angielski'],
      universities: ['ASP Warszawa', 'ASP Kraków', 'SWPS', 'Uniwersytet WSPA']
    },
    {
      id: 5,
      name: 'Lekarz',
      category: 'Zdrowie i Opieka',
      description:
        'Diagnozowanie, leczenie i zapobieganie chorobom, opieka nad pacjentami w różnych specjalizacjach medycznych.',
      requirements: [
        'Bardzo dobra znajomość nauk ścisłych',
        'Odporność na stres',
        'Empatia i komunikatywność',
        'Gotowość do długotrwałej nauki'
      ],
      skills: [
        'Diagnostyka',
        'Farmakologia',
        'Anatomia',
        'Procedury medyczne',
        'Komunikacja z pacjentem'
      ],
      averageSalary: '8 000 - 20 000 zł',
      demandLevel: 'Wysoki',
      educationPath: 'Medycyna (6 lat), specjalizacja (4-6 lat), staże',
      workEnvironment: 'Szpital, przychodnia, gabinet prywatny, pogotowie',
      careerProspects:
        'Bardzo dobre perspektywy, możliwość specjalizacji, własna praktyka',
      matchPercentage: 91,
      isFavorite: true,
      requiredSubjects: ['Biologia (rozszerzona)', 'Chemia (rozszerzona)', 'Matematyka'],
      universities: [
        'Warszawski Uniwersytet Medyczny',
        'Uniwersytet Jagielloński CM',
        'Gdański Uniwersytet Medyczny'
      ]
    },
    {
      id: 6,
      name: 'Nauczyciel',
      category: 'Edukacja',
      description:
        'Przekazywanie wiedzy i kształtowanie postaw uczniów, planowanie lekcji, ocenianie postępów w nauce.',
      requirements: [
        'Umiejętności komunikacyjne',
        'Cierpliwość i wytrwałość',
        'Kreatywność w nauczaniu',
        'Znajomość przedmiotu nauczania'
      ],
      skills: [
        'Dydaktyka',
        'Psychologia rozwojowa',
        'Zarządzanie klasą',
        'Technologie edukacyjne',
        'Ocenianie'
      ],
      averageSalary: '3 500 - 6 000 zł',
      demandLevel: 'Średni',
      educationPath: 'Studia kierunkowe + przygotowanie pedagogiczne, praktyki',
      workEnvironment: 'Szkoła podstawowa, gimnazjum, liceum, przedszkole',
      careerProspects:
        'Stabilne zatrudnienie, możliwość awansu na stanowiska kierownicze',
      matchPercentage: 73,
      isFavorite: false,
      requiredSubjects: ['Przedmiot nauczania', 'Język polski', 'Pedagogika'],
      universities: [
        'Uniwersytet Warszawski',
        'Uniwersytet Jagielloński',
        'Uniwersytet im. A. Mickiewicza'
      ]
    }
  ]

  const categories = [
    'Wszystkie',
    'IT i Technologie',
    'Zdrowie i Opieka',
    'Inżynieria',
    'Kreatywne',
    'Edukacja'
  ]

  const filteredProfessions = professions.filter((profession) => {
    const matchesSearch =
      profession.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profession.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory =
      selectedCategory === 'Wszystkie' || profession.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const getDemandColor = (level: string) => {
    switch (level) {
      case 'Wysoki':
        return 'text-green-600 bg-green-50 border-green-200'
      case 'Średni':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'Niski':
        return 'text-red-600 bg-red-50 border-red-200'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  if (selectedProfession) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-6">
            <button
              className="mb-4 flex items-center text-[#2bcaca] hover:text-[#229999]"
              onClick={() => setSelectedProfession(null)}
            >
              <ChevronRight className="mr-1 h-4 w-4 rotate-180 transform" />
              Powrót do listy zawodów
            </button>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {selectedProfession.name}
                </h1>
                <p className="mt-1 text-gray-600">{selectedProfession.category}</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-[#2bcaca]">
                  {selectedProfession.matchPercentage}%
                </div>
                <p className="text-sm text-gray-500">dopasowanie</p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Left Column */}
            <div className="space-y-6 lg:col-span-2">
              {/* Description */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Opis zawodu</h2>
                <p className="leading-relaxed text-gray-700">
                  {selectedProfession.description}
                </p>
              </div>

              {/* Requirements */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Wymagania</h2>
                <ul className="space-y-2">
                  {selectedProfession.requirements.map((req, index) => (
                    <li className="flex items-start" key={index}>
                      <Target className="mt-1 mr-2 h-4 w-4 flex-shrink-0 text-[#2bcaca]" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Skills */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Kluczowe umiejętności</h2>
                <div className="flex flex-wrap gap-2">
                  {selectedProfession.skills.map((skill, index) => (
                    <Badge
                      className="border-[#2bcaca]/20 bg-[#2bcaca]/10 text-[#2bcaca] rounded-sm"
                      key={index}
                      variant="outline"
                    >
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Education Path */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Ścieżka edukacyjna</h2>
                <p className="mb-4 text-gray-700">{selectedProfession.educationPath}</p>

                <h3 className="mb-2 font-medium">Wymagane przedmioty maturalne:</h3>
                <div className="mb-4 flex flex-wrap gap-2">
                  {selectedProfession.requiredSubjects.map((subject, index) => (
                    <Badge
                      className="border-[#2bcaca]/20 bg-[#2bcaca]/10 text-[#2bcaca] rounded-sm"
                      key={index}
                      variant="outline"
                    >
                      {subject}
                    </Badge>
                  ))}
                </div>

                <h3 className="mb-2 font-medium">Rekomendowane uczelnie:</h3>
                <ul className="space-y-1">
                  {selectedProfession.universities.map((uni, index) => (
                    <li className="flex items-center text-gray-700" key={index}>
                      <GraduationCap className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      {uni}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Kluczowe informacje</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <DollarSign className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-600">Zarobki</span>
                    </div>
                    <span className="font-medium">
                      {selectedProfession.averageSalary}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <TrendingUp className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-600">Zapotrzebowanie</span>
                    </div>
                    <Badge
                      className={getDemandColor(selectedProfession.demandLevel)}
                      variant="outline"
                    >
                      {selectedProfession.demandLevel}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <MapPin className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-600">Środowisko pracy</span>
                    </div>
                  </div>
                  <p className="ml-6 text-sm text-gray-700">
                    {selectedProfession.workEnvironment}
                  </p>
                </div>
              </div>

              {/* Career Prospects */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Perspektywy kariery</h2>
                <p className="text-sm leading-relaxed text-gray-700">
                  {selectedProfession.careerProspects}
                </p>
              </div>

              {/* Actions */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Działania</h2>
                <div className="space-y-3">
                  <Button className="w-full bg-[#2bcaca] text-white hover:bg-[#229999] rounded-sm">
                    <Heart className="mr-2 h-4 w-4" />
                    Dodaj do ulubionych
                  </Button>
                  <Button className="w-full rounded-sm" variant="outline">
                    <BookOpen className="mr-2 h-4 w-4" />
                    Zobacz kierunki studiów
                  </Button>
                  <Button className="w-full rounded-sm" variant="outline">
                    <Users className="mr-2 h-4 w-4" />
                    Znajdź mentora
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Proponowane zawody</h1>
          <p className="text-gray-600">
            Odkryj zawody dopasowane do Twoich predyspozycji i zainteresowań
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                <Input
                  className="pl-10 border-gray-200"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Szukaj zawodu..."
                  value={searchTerm}
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  className={cn(
                    selectedCategory === category
                      ? 'bg-[#2bcaca] text-white hover:bg-[#229999]'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  size="sm"
                  variant={selectedCategory === category ? 'default' : 'outline'}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredProfessions.map((profession) => (
            <div
              className="cursor-pointer border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md flex flex-col h-full"
              key={profession.id}
              onClick={() => setSelectedProfession(profession)}
            >
              {/* Header */}
              <div className="mb-4 flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="mb-1 font-semibold text-gray-900">{profession.name}</h3>
                  <p className="text-sm text-gray-500">{profession.category}</p>
                </div>
                <div className="ml-4 text-right">
                  <div className="text-lg font-bold text-[#2bcaca]">
                    {profession.matchPercentage}%
                  </div>
                  <p className="text-xs text-gray-500">dopasowanie</p>
                </div>
              </div>

              {/* Description */}
              <p className="mb-4 line-clamp-3 text-sm text-gray-700 flex-grow">
                {profession.description}
              </p>

              {/* Stats */}
              <div className="mb-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Zarobki:</span>
                  <span className="font-medium">{profession.averageSalary}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Zapotrzebowanie:</span>
                  <Badge
                    className={getDemandColor(profession.demandLevel)}
                    size="sm"
                    variant="outline"
                  >
                    {profession.demandLevel}
                  </Badge>
                </div>
              </div>

              {/* Skills Preview */}
              <div className="mb-4">
                <p className="mb-2 text-xs text-gray-500">Kluczowe umiejętności:</p>
                <div className="flex flex-wrap gap-1">
                  {profession.skills.slice(0, 3).map((skill, index) => (
                    <Badge className="text-xs" key={index} size="sm" variant="outline">
                      {skill}
                    </Badge>
                  ))}
                  {profession.skills.length > 3 && (
                    <Badge className="text-xs" size="sm" variant="outline">
                      +{profession.skills.length - 3}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Action */}
              <Button className="w-full rounded-sm mt-auto" size="sm" variant="outline">
                <Briefcase className="mr-2 h-4 w-4" />
                Zobacz szczegóły
              </Button>
            </div>
          ))}
        </div>

        {filteredProfessions.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-gray-500">
              Nie znaleziono zawodów spełniających kryteria wyszukiwania.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
